import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  Sparkles,
  Rocket,
  Building,
  ArrowRight,
  Infinity,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import { onboardingService } from '@/services/onboardingService';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import type { SubscriptionPlan } from '@/types/subscription';

// Map iconName from seed data to lucide icon components
const ICON_MAP: Record<string, React.ElementType> = {
  sparkles: Sparkles,
  rocket: Rocket,
  building: Building,
};

/** Render a limit value: -1 or null/undefined = "Unlimited" */
const formatLimit = (value: number | undefined | null): React.ReactNode => {
  if (value === undefined || value === null || value === -1) {
    return (
      <span className="inline-flex items-center gap-1">
        <Infinity className="h-3.5 w-3.5" />
        Unlimited
      </span>
    );
  }
  return value.toLocaleString();
};

const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';
const PLAN_CODE_STORAGE_KEY = 'five_ai_onboarding_plan_code';
const SELECTED_PLAN_DATA_KEY = 'five_ai_onboarding_selected_plan_data';

interface SubscriptionPlanSelectionPageProps {
  sessionId: string;
  onPlanSelected: (planCode: string) => void;
  onContinue?: () => void; // Optional callback to continue to consent page
}

export const SubscriptionPlanSelectionPage: React.FC<SubscriptionPlanSelectionPageProps> = ({
  sessionId,
  onPlanSelected,
  onContinue,
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [billingToggle, setBillingToggle] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch active subscription plans (public onboarding endpoint)
        const response = await onboardingService.getSubscriptionPlans();
        
        // Handle different response formats
        let plansData: SubscriptionPlan[];
        if (Array.isArray(response)) {
          plansData = response as SubscriptionPlan[];
        } else {
          const res = response as Record<string, unknown> & { content?: SubscriptionPlan[]; plans?: SubscriptionPlan[] };
          plansData = Array.isArray(res?.content) ? res.content : Array.isArray(res?.plans) ? res.plans : [];
        }
        
        // Filter to only active plans and sort by displayOrder
        const activePlans = plansData
          .filter((plan: SubscriptionPlan) => plan.status === 'ACTIVE')
          .sort((a: SubscriptionPlan, b: SubscriptionPlan) => 
            (a.displayOrder || 0) - (b.displayOrder || 0)
          );
        
        setPlans(activePlans);
        console.log('[SubscriptionPlan] Loaded active plans:', activePlans);
      } catch (err: unknown) {
        console.error('[SubscriptionPlan] Failed to fetch plans:', err);
        setError((err as Error)?.message ?? 'Failed to load subscription plans. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) {
      fetchPlans();
    }
  }, [sessionId]);

  const handlePlanSelect = async (plan: SubscriptionPlan) => {
    const planCode = plan.planCode;
    if (!planCode) {
      setError('This plan is missing a plan code. Please try another plan or contact support.');
      return;
    }

    // Ensure billingEmail is aligned with user email (as required)
    let billingEmail: string | undefined;
    try {
      const raw = localStorage.getItem(LOCAL_USER_INFO_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<{ signUpEmail?: string }>;
        if (parsed.signUpEmail) billingEmail = parsed.signUpEmail;
      }
    } catch {
      // ignore
    }

    setSelectedPlanCode(planCode);
    setIsSubmitting(true);
    setError(null);

    try {
      console.log('[SubscriptionPlan] Selecting plan:', { planCode, planId: plan.id });
      
      // Extract countryCode and paymentGateway from plan
      const countryCode = plan.supportedCountries && plan.supportedCountries.length > 0 
        ? plan.supportedCountries[0] 
        : undefined;
      const paymentGateway = plan.supportedPaymentGateways && plan.supportedPaymentGateways.length > 0
        ? plan.supportedPaymentGateways[0]
        : undefined;

      // Use the billing toggle value, falling back to plan's own billingCycle or MONTHLY
      const planBillingCycle = billingToggle || plan.billingCycle || 'MONTHLY';
      const planCurrency = plan.currency || 'USD';

      if (!plan.billingCycle) {
        console.log('[SubscriptionPlan] billingCycle missing from plan API, defaulting to MONTHLY');
      }
      if (!plan.currency) {
        console.log('[SubscriptionPlan] currency missing from plan API, defaulting to USD');
      }

      console.log('[SubscriptionPlan] Plan details:', {
        planCode,
        billingCycle: planBillingCycle,
        currency: planCurrency,
        countryCode,
        paymentGateway,
      });

      // Update subscription in onboarding session
      await onboardingService.updateSubscription(sessionId, {
        // IMPORTANT: API expects SubscriptionUpdateRequest fields (planCode, billingCycle, etc.)
        // NOT a planId. Using planId causes backend 403.
        planCode,
        billingEmail: billingEmail || undefined,
        billingCycle: planBillingCycle,
        currency: planCurrency,
        countryCode,
        paymentGateway,
      });

      console.log('[SubscriptionPlan] ✅ Plan selected successfully');
      
      try {
        localStorage.setItem(PLAN_CODE_STORAGE_KEY, planCode);
        // Store full plan data for use in complete payload
        // billingCycle and currency default when missing from plan API
        const planData: {
          planCode: string;
          billingEmail?: string;
          billingCycle: string;
          currency: string;
          countryCode?: string;
          paymentGateway?: string;
        } = {
          planCode,
          billingCycle: planBillingCycle,
          currency: planCurrency,
          ...(billingEmail && { billingEmail }),
          ...(countryCode && { countryCode }),
          ...(paymentGateway && { paymentGateway }),
        };
        console.log('[SubscriptionPlan] Storing plan data:', planData);
        localStorage.setItem(SELECTED_PLAN_DATA_KEY, JSON.stringify(planData));
      } catch {
        // ignore
      }

      // Stop the loading state so UI can proceed.
      setIsSubmitting(false);

      // Proceed to the next step (preferences/consent page) immediately.
      if (onContinue) {
        onContinue();
        return;
      }

      // Fallback: notify parent that a plan has been selected.
      onPlanSelected(planCode);
    } catch (err: unknown) {
      console.error('[SubscriptionPlan] Failed to select plan:', err);
      setError((err as Error)?.message ?? 'Failed to select plan. Please try again.');
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    }).format(price);
  };

  const getFeaturesList = (plan: SubscriptionPlan): string[] => {
    if (!plan.features) return [];
    // If features is an array, return it
    if (Array.isArray(plan.features)) return plan.features;
    // If features is an object, convert to array of strings
    if (typeof plan.features === 'object') {
      return Object.entries(plan.features).map(([key, value]) => {
        if (typeof value === 'string') return value;
        if (typeof value === 'boolean' && value) return key;
        return `${key}: ${value}`;
      });
    }
    return [];
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <BrandedLoading message="Loading subscription plans…" iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error && plans.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <div className="text-center space-y-4 max-w-md">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="flex flex-col items-center py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container-custom max-w-7xl w-full px-4 sm:px-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 space-y-4"
          >
            <h1 className="text-3xl sm:text-5xl font-bold text-foreground tracking-tight">
              Choose Your Plan
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Select the subscription plan that best fits your career development journey
            </p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center">
              {error}
            </div>
          )}

          {/* Billing cycle toggle */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex justify-center mb-8"
          >
            <div className="inline-flex items-center bg-muted rounded-lg p-1">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingToggle === 'MONTHLY'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setBillingToggle('MONTHLY')}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingToggle === 'YEARLY'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setBillingToggle('YEARLY')}
              >
                Yearly
                {(() => {
                  // Show max savings percentage across plans
                  const maxSavings = plans.reduce((max, p) => Math.max(max, p.savingsPercentage ?? 0), 0);
                  if (maxSavings > 0) {
                    return (
                      <span className="ml-1 text-xs text-emerald-600 font-semibold">
                        Save {maxSavings}%
                      </span>
                    );
                  }
                  return null;
                })()}
              </button>
            </div>
          </motion.div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
            {plans.map((plan, index) => {
              const isSelected = !!selectedPlanCode && selectedPlanCode === plan.planCode;
              const isPopular = plan.isPopular || index === Math.floor(plans.length / 2);
              const badgeLabel = plan.badgeText || (isPopular ? 'Most Popular' : null);
              const monthlyPrice = plan.monthlyPrice;
              const yearlyPerMonth = plan.yearlyPrice ? Math.round((plan.yearlyPrice / 12) * 100) / 100 : null;
              const displayPrice = billingToggle === 'YEARLY' && yearlyPerMonth !== null ? yearlyPerMonth : monthlyPrice;
              const hasTrial = plan.trialEnabled && plan.trialDurationDays && plan.trialDurationDays > 0;
              const featureHighlights = plan.featureHighlights ?? [];
              const legacyFeatures = featureHighlights.length === 0 ? getFeaturesList(plan) : [];
              const IconComponent = plan.iconName ? ICON_MAP[plan.iconName] ?? Sparkles : null;
              const isEnterprise = plan.planType === 'ENTERPRISE';
              const isFree = plan.planType === 'FREE' || monthlyPrice === 0;

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className={badgeLabel ? 'md:-mt-4' : ''}
                >
                  <Card
                    className={`relative h-full rounded-2xl transition-all duration-300 cursor-pointer ${
                      isSelected
                        ? 'border-2 border-primary shadow-2xl bg-gradient-to-br from-background to-primary/5 scale-105'
                        : badgeLabel
                        ? 'border-2 border-primary/50 shadow-lg hover:shadow-xl hover:border-primary/70'
                        : 'border-2 shadow-md hover:shadow-lg hover:border-primary/30'
                    }`}
                    onClick={() => !isSubmitting && handlePlanSelect(plan)}
                  >
                    {/* Badge ribbon */}
                    {badgeLabel && !isSelected && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                        <Badge className="bg-gradient-to-r from-purple-600 to-primary text-white border-0 px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1 shadow-lg">
                          <Sparkles className="w-3.5 h-3.5" />
                          {badgeLabel}
                        </Badge>
                      </div>
                    )}

                    <CardHeader className="text-center pb-4 space-y-4">
                      {/* Icon */}
                      {IconComponent && (
                        <div className="flex justify-center">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <IconComponent className="h-6 w-6 text-primary" />
                          </div>
                        </div>
                      )}

                      <CardTitle className="text-2xl font-bold">{plan.displayName}</CardTitle>

                      {/* Tagline */}
                      {plan.tagline && (
                        <p className="text-sm font-medium text-primary/80">{plan.tagline}</p>
                      )}

                      <CardDescription className="text-base min-h-[2rem]">
                        {plan.description}
                      </CardDescription>

                      {/* Price */}
                      <div className="space-y-2">
                        {isFree ? (
                          <div className="flex items-baseline justify-center gap-2">
                            <span className="text-4xl sm:text-5xl font-bold">$0</span>
                            <span className="text-muted-foreground text-lg">/mo</span>
                          </div>
                        ) : isEnterprise ? (
                          <div className="flex items-baseline justify-center">
                            <span className="text-3xl sm:text-4xl font-bold">Custom</span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-baseline justify-center gap-2">
                              <span className="text-4xl sm:text-5xl font-bold">
                                {formatPrice(displayPrice, plan.currency)}
                              </span>
                              <span className="text-muted-foreground text-lg">/mo</span>
                            </div>
                            {billingToggle === 'YEARLY' && yearlyPerMonth !== null && (
                              <p className="text-xs text-muted-foreground">
                                {formatPrice(plan.yearlyPrice, plan.currency)}/yr &middot; billed yearly
                              </p>
                            )}
                            {billingToggle === 'YEARLY' && plan.savingsPercentage && plan.savingsPercentage > 0 && (
                              <Badge variant="secondary" className="text-emerald-600 bg-emerald-50 dark:bg-emerald-950 border-0">
                                Save {plan.savingsPercentage}%
                              </Badge>
                            )}
                          </>
                        )}

                        {/* Trial Badge */}
                        {hasTrial && !isFree && (
                          <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                            {plan.trialDurationDays}-day free trial
                          </div>
                        )}
                      </div>

                      {/* Recommended For */}
                      {plan.recommendedFor && (
                        <p className="text-xs text-muted-foreground italic">
                          Best for: {plan.recommendedFor}
                        </p>
                      )}
                    </CardHeader>

                    <CardContent className="space-y-6">
                      {/* Feature Highlights (new field) */}
                      {featureHighlights.length > 0 && (
                        <ul className="space-y-3">
                          {featureHighlights.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="h-5 w-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Legacy features fallback */}
                      {legacyFeatures.length > 0 && (
                        <ul className="space-y-3">
                          {legacyFeatures.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="h-5 w-5 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Token & session limits */}
                      {(plan.dailyTokenLimit !== undefined || plan.monthlyTokenLimit !== undefined || plan.maxCoachSessionsPerDay !== undefined || plan.maxLearningPaths !== undefined) && (
                        <div className="pt-4 border-t space-y-2">
                          <p className="text-[11px] font-semibold text-muted-foreground uppercase">Limits</p>
                          <ul className="space-y-2">
                            {plan.dailyTokenLimit !== undefined && (
                              <li className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Daily Tokens</span>
                                <span className="font-medium">{formatLimit(plan.dailyTokenLimit)}</span>
                              </li>
                            )}
                            {plan.monthlyTokenLimit !== undefined && (
                              <li className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Monthly Tokens</span>
                                <span className="font-medium">{formatLimit(plan.monthlyTokenLimit)}</span>
                              </li>
                            )}
                            {plan.maxCoachSessionsPerDay !== undefined && (
                              <li className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Coach Sessions/Day</span>
                                <span className="font-medium">{formatLimit(plan.maxCoachSessionsPerDay)}</span>
                              </li>
                            )}
                            {plan.maxLearningPaths !== undefined && (
                              <li className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Learning Paths</span>
                                <span className="font-medium">{formatLimit(plan.maxLearningPaths)}</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Legacy limits fallback */}
                      {plan.limits && Object.keys(plan.limits).length > 0 && plan.dailyTokenLimit === undefined && plan.monthlyTokenLimit === undefined && (
                        <div className="pt-4 border-t space-y-2">
                          <p className="text-[11px] font-semibold text-muted-foreground uppercase">Limits</p>
                          <ul className="space-y-2">
                            {Object.entries(plan.limits).map(([key, value]) => (
                              <li key={key} className="flex items-center justify-between text-[11px]">
                                <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                                <span className="text-foreground font-medium">{value === -1 ? formatLimit(-1) : value}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Select Button */}
                      <Button
                        variant={isSelected ? 'default' : badgeLabel ? 'default' : 'outline'}
                        className={`w-full h-12 text-base font-semibold ${
                          isSelected ? 'bg-primary text-primary-foreground' : ''
                        }`}
                        disabled={isSubmitting}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePlanSelect(plan);
                        }}
                      >
                        {isSubmitting && isSelected ? (
                          <>
                            <BrandedSpinner className="mr-2" />
                            Processing...
                          </>
                        ) : isSelected ? (
                          <>
                            <Check className="mr-2 h-4 w-4 text-green-200" />
                            Selected
                          </>
                        ) : isEnterprise ? (
                          <>
                            {plan.ctaText || 'Contact Sales'} <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        ) : isFree ? (
                          <>
                            {plan.ctaText || 'Get Started'} <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        ) : (
                          <>
                            {plan.ctaText || 'Select Plan'} <ArrowRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Continue Button - Show if plan is selected */}
          {selectedPlanCode && !isSubmitting && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center"
            >
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  // If continue callback exists, use it; otherwise call onPlanSelected
                  if (onContinue) {
                    onContinue();
                  } else {
                    onPlanSelected(selectedPlanCode);
                  }
                }}
                className="min-w-[200px] h-14 text-base font-semibold"
              >
                Continue to Preferences <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SubscriptionPlanSelectionPage;
